import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MessagesSquare,
  ClipboardList,
  Scale,
  CalendarRange,
  DollarSign,
  BrainCircuit,
  Wrench,
  Settings as SettingsIcon,
  MoreHorizontal,
  LogOut,
  X,
} from "lucide-react";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

const primaryNav = [
  { to: "/app/today", label: "Today", icon: LayoutDashboard, testid: "nav-today" },
  { to: "/app/command", label: "Command", icon: ClipboardList, testid: "nav-command" },
  { to: "/app/sarge", label: "Sarge", icon: MessagesSquare, testid: "nav-sarge" },
];

const desktopExtra = [
  { to: "/app/decisions", label: "Decisions", icon: Scale, testid: "nav-decisions" },
  { to: "/app/review", label: "Review", icon: CalendarRange, testid: "nav-review" },
  { to: "/app/financial", label: "Financial", icon: DollarSign, testid: "nav-financial" },
  { to: "/app/memory", label: "Knowledge", icon: BrainCircuit, testid: "nav-memory" },
  { to: "/app/tools", label: "Files & Tools", icon: Wrench, testid: "nav-tools" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const moreItems = [
  { to: "/app/decisions", label: "Decisions", icon: Scale, testid: "more-decisions" },
  { to: "/app/review", label: "Review", icon: CalendarRange, testid: "more-review" },
  { to: "/app/financial", label: "Financial", icon: DollarSign, testid: "more-financial" },
  { to: "/app/memory", label: "Knowledge", icon: BrainCircuit, testid: "more-memory" },
  { to: "/app/tools", label: "Files & Tools", icon: Wrench, testid: "more-tools" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "more-settings" },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors wr-mono text-sm ${
          isActive
            ? "border-wr-red bg-wr-redMuted text-white"
            : "border-transparent text-wr-sub hover:text-white hover:bg-wr-surface2"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.6} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  // Defense-in-depth: authenticated app surfaces must never be indexed. Set robots=noindex
  // while an AppShell page is mounted; restore the public default on unmount.
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "noindex,nofollow");
    return () => { if (meta) meta.setAttribute("content", prev || "index,follow"); };
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-wr-bg flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-60 shrink-0 border-r border-wr-border bg-wr-header sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-wr-border">
          <Wordmark to="/app/brief" size="sm" showTagline />
        </div>
        <nav className="flex-1 py-4 flex flex-col gap-1">
          {primaryNav.map((i) => (
            <NavItem key={i.to} item={i} />
          ))}
          <div className="my-3 border-t border-wr-border mx-3" />
          {desktopExtra.map((i) => (
            <NavItem key={i.label} item={i} />
          ))}
        </nav>
        <div className="border-t border-wr-border p-3">
          <div className="px-3 py-2 mb-2">
            <p className="wr-label !text-[0.6rem]">Founder</p>
            <p className="text-sm text-white truncate" data-testid="sidebar-user-name">
              {user?.name || user?.email}
            </p>
            <p className="text-xs text-wr-muted truncate">{user?.workspace?.name}</p>
          </div>
          <button
            onClick={doLogout}
            data-testid="sidebar-logout"
            className="w-full flex items-center gap-2 px-3 py-2 wr-mono text-sm text-wr-sub hover:text-wr-red hover:bg-wr-surface2 transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 bg-black/90 backdrop-blur-xl border-b border-wr-border px-4 py-3 flex items-center justify-between safe-top">
          <Wordmark to="/app/brief" size="sm" />
        </header>

        <main className="flex-1 min-w-0 pb-24 md:pb-0">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur-xl border-t border-wr-border grid grid-cols-4 safe-bottom"
        data-testid="mobile-bottom-nav"
      >
        {primaryNav.map((i) => {
          const Icon = i.icon;
          return (
            <NavLink
              key={i.to}
              to={i.to}
              data-testid={`${i.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 wr-mono text-[0.62rem] uppercase tracking-wider ${
                  isActive ? "text-wr-red" : "text-wr-sub"
                }`
              }
            >
              <Icon size={20} strokeWidth={1.6} />
              {i.label}
            </NavLink>
          );
        })}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="nav-more-mobile"
          className="flex flex-col items-center justify-center gap-1 py-2.5 wr-mono text-[0.62rem] uppercase tracking-wider text-wr-sub"
        >
          <MoreHorizontal size={20} strokeWidth={1.6} />
          More
        </button>
      </nav>

      {/* Mobile "More" sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="more-sheet">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMoreOpen(false)}
          />
          <div className="absolute bottom-0 inset-x-0 bg-wr-surface border-t border-wr-border p-4 safe-bottom wr-fade-up">
            <div className="flex items-center justify-between mb-4">
              <span className="wr-label">Menu</span>
              <button onClick={() => setMoreOpen(false)} data-testid="more-close" className="text-wr-sub">
                <X size={20} />
              </button>
            </div>
            <div className="flex flex-col">
              {moreItems.map((i) => (
                <NavItem key={i.label} item={i} onClick={() => setMoreOpen(false)} />
              ))}
              <button
                onClick={doLogout}
                data-testid="more-logout"
                className="flex items-center gap-3 px-3 py-3 wr-mono text-sm text-wr-red border-l-2 border-transparent hover:bg-wr-surface2"
              >
                <LogOut size={18} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
